import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { 
  MessageSquare, 
  MousePointerClick,
  Calendar,
  ChevronDown,
  RefreshCw,
  Users,
  Globe,
  TrendingUp,
  ArrowRight,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIMEFRAME_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

const CHART_COLORS = {
  t1: '#3b82f6',           // blue for T1 clicks
  conversations: '#22c55e', // green for conversations
  conversions: '#f59e0b',   // amber for conversions/subs
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default function CupidBot() {
  const [timeframe, setTimeframe] = useState('this-week');
  const [selectedModel, setSelectedModel] = useState(null); // null = Global
  const [selectedSource, setSelectedSource] = useState(null); // null = All traffic
  const [startDate, setStartDate] = useState(daysAgoISO(7));
  const [endDate, setEndDate] = useState(todayISO());
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [models, setModels] = useState([]);
  const [sources, setSources] = useState([]);
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  const fetchModels = useCallback(async () => {
    try {
      const [modelsRes, sourcesRes] = await Promise.all([
        axios.get(`${API_URL}/cupidbot/models`),
        axios.get(`${API_URL}/cupidbot/traffic-sources`),
      ]);
      setModels(modelsRes.data.models || []);
      setSources(sourcesRes.data.sources || []);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  }, []);

  const isCustomRange = timeframe === 'custom';
  const customRangeReady = !isCustomRange || (startDate && endDate && startDate <= endDate);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (timeframe === 'custom' && !(startDate && endDate && startDate <= endDate)) {
      setLoading(false);
      return;
    }

    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (timeframe === 'custom') {
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      } else {
        params.set('timeframe', timeframe);
      }
      if (selectedModel) params.set('model_id', selectedModel);
      if (selectedSource) params.set('source_id', selectedSource);
      const qs = params.toString();
      
      const [overviewRes, timelineRes, funnelRes, cacheRes] = await Promise.all([
        axios.get(`${API_URL}/cupidbot/analytics/overview?${qs}`),
        axios.get(`${API_URL}/cupidbot/analytics/timeline?${qs}`),
        axios.get(`${API_URL}/cupidbot/analytics/funnel?${qs}`),
        axios.get(`${API_URL}/cupidbot/cache/status`)
      ]);
      
      setOverview(overviewRes.data);
      setTimeline(timelineRes.data.timeline || []);
      setFunnel(funnelRes.data);
      setCacheStatus(cacheRes.data);
    } catch (error) {
      console.error('Error fetching CupidBot data:', error);
      toast.error('Failed to load CupidBot analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeframe, selectedModel, selectedSource, startDate, endDate]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/cupidbot/cache/refresh`);
      toast.success('Cache refresh started');
      // Wait a bit then refetch
      setTimeout(() => fetchData(true), 2000);
    } catch (error) {
      toast.error('Failed to refresh cache');
      setRefreshing(false);
    }
  };

  const getTimeframeLabel = () => {
    return TIMEFRAME_OPTIONS.find(opt => opt.value === timeframe)?.label || timeframe;
  };

  const getModelLabel = () => {
    if (!selectedModel) return 'Global';
    const model = models.find(m => m.id === selectedModel);
    return model?.name || selectedModel;
  };

  const getSourceLabel = () => {
    if (!selectedSource) return 'All Traffic';
    const source = sources.find(s => s.id === selectedSource);
    return source?.name || selectedSource;
  };

  // Only show sources belonging to the selected model (sources with no model
  // attached stay visible, they apply everywhere).
  const visibleSources = selectedModel
    ? sources.filter(s => !s.model_id || s.model_id === selectedModel)
    : sources;

  const selectSource = (source) => {
    setSelectedSource(source ? source.id : null);
    // A source belongs to a model, so keep the model selector in sync.
    if (source?.model_id) setSelectedModel(source.model_id);
  };

  // GMS only tracks T1 clicks for Threads traffic — the backend tells us when
  // the top of the funnel doesn't apply to the current selection.
  const showT1 = overview?.t1_applicable !== false;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value?.toLocaleString() || 0}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ChartComponent = chartType === 'bar' ? BarChart : LineChart;
  const DataComponent = chartType === 'bar' ? Bar : Line;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="cupidbot-loading">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cupidbot-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">CupidBot Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Telegram conversion funnel insights
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Model Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-white" data-testid="model-selector">
                <Users className="w-4 h-4 mr-2" />
                {getModelLabel()}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
              <DropdownMenuItem
                onClick={() => setSelectedModel(null)}
                className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                  !selectedModel ? 'bg-zinc-800 text-white' : ''
                }`}
              >
                Global (All Models)
              </DropdownMenuItem>
              {models.map(model => (
                <DropdownMenuItem
                  key={model.id}
                  onClick={() => setSelectedModel(model.id)}
                  className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                    selectedModel === model.id ? 'bg-zinc-800 text-white' : ''
                  }`}
                >
                  {model.name}
                </DropdownMenuItem>
              ))}
              {models.length === 0 && (
                <DropdownMenuItem disabled className="text-zinc-500">
                  No models configured
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Traffic Source Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-white" data-testid="source-selector">
                <Globe className="w-4 h-4 mr-2" />
                {getSourceLabel()}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
              <DropdownMenuItem
                onClick={() => selectSource(null)}
                className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                  !selectedSource ? 'bg-zinc-800 text-white' : ''
                }`}
              >
                All Traffic
              </DropdownMenuItem>
              {visibleSources.map(source => (
                <DropdownMenuItem
                  key={source.id}
                  onClick={() => selectSource(source)}
                  className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                    selectedSource === source.id ? 'bg-zinc-800 text-white' : ''
                  }`}
                >
                  {source.name}
                  <span className="ml-2 text-xs text-zinc-500">
                    {source.account_ids?.length || 0} acc.
                  </span>
                </DropdownMenuItem>
              ))}
              {visibleSources.length === 0 && (
                <DropdownMenuItem disabled className="text-zinc-500">
                  No traffic sources configured
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Chart Type Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chartType === 'line' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="chart-type-line"
            >
              Line
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chartType === 'bar' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="chart-type-bar"
            >
              Bar
            </button>
          </div>
          
          {/* Timeframe Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-white" data-testid="timeframe-selector">
                <Calendar className="w-4 h-4 mr-2" />
                {getTimeframeLabel()}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
              {TIMEFRAME_OPTIONS.map(option => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setTimeframe(option.value)}
                  className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                    timeframe === option.value ? 'bg-zinc-800 text-white' : ''
                  }`}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Custom Date Range */}
          {isCustomRange && (
            <div className="flex items-center gap-2" data-testid="custom-range">
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="start-date-input"
              />
              <span className="text-zinc-500 text-sm">→</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="end-date-input"
              />
            </div>
          )}
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!customRangeReady && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-400" data-testid="invalid-range-warning">
          Pick a start date and an end date (start must come first) to load the range.
        </div>
      )}

      {!showT1 && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-sm text-zinc-400" data-testid="no-t1-notice">
          T1 clicks are only tracked for Threads traffic, so the click stage of the
          funnel is hidden for <span className="text-white">{getSourceLabel()}</span>.
        </div>
      )}

      {/* KPI Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${showT1 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'}`}>
        {/* T1 Clicks */}
        {showT1 && (
        <div className="bg-card p-4 rounded-lg border border-blue-500/20" data-testid="kpi-t1-clicks">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <MousePointerClick className="w-5 h-5 text-blue-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">T1 Clicks</p>
            <p className="text-2xl font-bold text-blue-500">{overview?.t1_clicks?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">From GMS tracking</p>
          </div>
        </div>
        )}

        {/* Conversations */}
        <div className="bg-card p-4 rounded-lg border border-green-500/20" data-testid="kpi-conversations">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">Conversations</p>
            <p className="text-2xl font-bold text-green-500">{overview?.conversations?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">Telegram chats started</p>
          </div>
        </div>

        {/* CVR (T1 → Conversation) */}
        {showT1 && (
        <div className="bg-card p-4 rounded-lg border border-purple-500/20" data-testid="kpi-cvr">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Percent className="w-5 h-5 text-purple-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">CVR (T1 → Conv)</p>
            <p className="text-2xl font-bold text-purple-500">{overview?.conv_from_t1_pct?.toFixed(2) || 0}%</p>
            <p className="text-xs text-zinc-500 mt-1">Click to conversation rate</p>
          </div>
        </div>
        )}

        {/* Subs CVR */}
        <div className="bg-card p-4 rounded-lg border border-amber-500/20" data-testid="kpi-subs-cvr">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <TrendingUp className="w-5 h-5 text-amber-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">Subs CVR (Conv → Sub)</p>
            <p className="text-2xl font-bold text-amber-500">{overview?.sub_from_conv_pct?.toFixed(2) || 0}%</p>
            <p className="text-xs text-zinc-500 mt-1">
              {overview?.conversions?.toLocaleString() || 0} subscriptions
            </p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart 1: T1 Clicks & Conversations */}
        <div className="bg-card p-6 rounded-lg border border-zinc-800" data-testid="chart-clicks-conversations">
          <h3 className="text-lg font-semibold text-white mb-4">
            {showT1 ? 'T1 Clicks & Conversations' : 'Conversations'}
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ChartComponent data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {showT1 && (
                <DataComponent 
                  type="monotone" 
                  dataKey="t1_clicks" 
                  name="T1 Clicks"
                  stroke={CHART_COLORS.t1} 
                  fill={CHART_COLORS.t1}
                  strokeWidth={2}
                  dot={chartType === 'line' ? { fill: CHART_COLORS.t1, r: 4 } : undefined}
                />
                )}
                <DataComponent 
                  type="monotone" 
                  dataKey="conversations" 
                  name="Conversations"
                  stroke={CHART_COLORS.conversations} 
                  fill={CHART_COLORS.conversations}
                  strokeWidth={2}
                  dot={chartType === 'line' ? { fill: CHART_COLORS.conversations, r: 4 } : undefined}
                />
              </ChartComponent>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Subscriptions Over Time */}
        <div className="bg-card p-6 rounded-lg border border-zinc-800" data-testid="chart-subscriptions">
          <h3 className="text-lg font-semibold text-white mb-4">Subscriptions Over Time</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ChartComponent data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <DataComponent 
                  type="monotone" 
                  dataKey="conversions" 
                  name="Subscriptions"
                  stroke={CHART_COLORS.conversions} 
                  fill={CHART_COLORS.conversions}
                  strokeWidth={2}
                  dot={chartType === 'line' ? { fill: CHART_COLORS.conversions, r: 4 } : undefined}
                />
              </ChartComponent>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Funnel Analysis */}
      <div className="bg-card p-6 rounded-lg border border-zinc-800" data-testid="funnel-analysis">
        <h3 className="text-lg font-semibold text-white mb-6">Conversion Funnel Analysis</h3>
        
        <div className={`grid grid-cols-1 gap-4 ${showT1 ? 'md:grid-cols-3' : 'md:grid-cols-1'}`}>
          {/* T1 Clicks → Conversations */}
          {showT1 && (
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-zinc-400">T1 Clicks</span>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-600" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-zinc-400">Conversations</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">
                {funnel?.funnel?.t1_to_conversation?.cvr || 0}%
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                {funnel?.t1_clicks?.toLocaleString() || 0} → {funnel?.conversations?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          )}

          {/* Conversations → Subscriptions */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-zinc-400">Conversations</span>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-600" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm text-zinc-400">Subscriptions</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">
                {funnel?.funnel?.conversation_to_conversion?.cvr || 0}%
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                {funnel?.conversations?.toLocaleString() || 0} → {funnel?.conversions?.toLocaleString() || 0}
              </p>
            </div>
          </div>

          {/* Full Funnel */}
          {showT1 && (
          <div className="bg-zinc-800/50 rounded-lg p-4 border border-amber-500/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-zinc-400">T1 Clicks</span>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-600" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm text-zinc-400">Subscriptions</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-500">
                {funnel?.funnel?.full_funnel_cvr || 0}%
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                Full funnel conversion rate
              </p>
            </div>
          </div>
          )}
        </div>

        {/* Funnel Visualization */}
        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-2">
            {showT1 && (
            <>
            <div className="bg-blue-500/20 border border-blue-500/40 rounded-lg px-4 py-2 text-center min-w-[120px]">
              <p className="text-2xl font-bold text-blue-400">{overview?.t1_clicks?.toLocaleString() || 0}</p>
              <p className="text-xs text-zinc-500">T1 Clicks</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="w-6 h-6 text-zinc-600" />
              <span className="text-xs text-zinc-500">{overview?.conv_from_t1_pct?.toFixed(1) || 0}%</span>
            </div>
            </>
            )}
            <div className="bg-green-500/20 border border-green-500/40 rounded-lg px-4 py-2 text-center min-w-[120px]">
              <p className="text-2xl font-bold text-green-400">{overview?.conversations?.toLocaleString() || 0}</p>
              <p className="text-xs text-zinc-500">Conversations</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="w-6 h-6 text-zinc-600" />
              <span className="text-xs text-zinc-500">{overview?.sub_from_conv_pct?.toFixed(1) || 0}%</span>
            </div>
            <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg px-4 py-2 text-center min-w-[120px]">
              <p className="text-2xl font-bold text-amber-400">{overview?.conversions?.toLocaleString() || 0}</p>
              <p className="text-xs text-zinc-500">Subscriptions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
